import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import moment from "moment-timezone";
import { MapAtLocationSummary } from "api/Locations";
import styles from "../styles.module.scss";

interface Props {
  address: string;
  maps: MapAtLocationSummary[];
  onOpen: (map: MapAtLocationSummary) => void;
  onStartNew: () => void;
  onCancel: () => void;
}

/**
 * Ad-hoc address open — Session 20.
 *
 * When a rep enters an address that already resolves to a Location we've
 * seen before, we surface every Map already saved at that site instead of
 * silently attaching new work to whichever isCurrent row happens to sort
 * first. The rep picks one to open, or starts a brand-new Map at the site.
 *
 * "Start a new map" is deliberately non-destructive: the new Map lives
 * side-by-side with existing ones under a fresh mapGroupId.
 */
const ExistingMapsChooserModal: React.FC<Props> = ({
  address,
  maps,
  onOpen,
  onStartNew,
  onCancel,
}) => {
  return (
    <Modal isOpen={true} toggle={onCancel} size="md" centered>
      <ModalHeader toggle={onCancel}>This address already exists</ModalHeader>
      <ModalBody>
        <p style={{ fontSize: 13, color: "#5D5E62", marginTop: 0 }}>
          There {maps.length === 1 ? "is" : "are"} {maps.length} map
          {maps.length === 1 ? "" : "s"} saved at{" "}
          <strong>{address || "this location"}</strong>. Open one to continue
          editing, or start a brand-new map at the same site — the existing
          maps won&apos;t be touched.
        </p>

        <div className={styles.versionList}>
          {maps.map((m) => (
            <div key={m.mapGroupId} className={styles.versionRow}>
              <div className={styles.versionRowMain}>
                <div className={styles.versionRowHeader}>
                  <strong>{m.name || "Untitled map"}</strong>
                  {m.clientName && (
                    <span className={styles.versionBadge}>{m.clientName}</span>
                  )}
                </div>
                <div className={styles.versionRowMeta}>
                  Edited {moment(m.updatedAt).fromNow()} · v{m.version}
                </div>
                <div className={styles.versionRowStats}>
                  {m.zoneCount} zone{m.zoneCount === 1 ? "" : "s"} ·{" "}
                  {m.totalCuYd.toFixed(1)} cu yd
                </div>
              </div>
              <BasicButton
                type="button"
                scheme="secondary"
                outline
                onClick={() => onOpen(m)}
                styles={{ margin: "0" }}
                keepText
              >
                Open
              </BasicButton>
            </div>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <BasicButton
          type="button"
          scheme="secondary"
          outline
          onClick={onCancel}
          styles={{ margin: "0" }}
        >
          Cancel
        </BasicButton>
        <BasicButton
          type="button"
          scheme="primary"
          onClick={onStartNew}
          styles={{ margin: "0" }}
        >
          Start a new map at this site
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default ExistingMapsChooserModal;
